// @ts-strict-ignore
import React, { useEffect } from 'react'
import mapboxgl, { LngLatBoundsLike } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useTheme } from '@mui/material/styles'
import ErrorBoundary from 'components/ErrorBoundary'

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_KEY

export interface IFreelancerLocationMapProps {
  locationLatitude: number
  locationLongitude: number
  locationBounds0: number
  locationBounds1: number
  locationBounds2: number
  locationBounds3: number
  dark?: boolean
  style?: React.CSSProperties
}

function FreelancerLocationMap(props: IFreelancerLocationMapProps) {
  const { locationLatitude, locationLongitude, locationBounds0, locationBounds1, locationBounds2, locationBounds3 } = props
  const theme = useTheme()

  const containerRef = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<mapboxgl.Map>(null)
  const locationMarkerRef = React.useRef<mapboxgl.Marker>(null)

  const STYLE: React.CSSProperties = {
    height: 300,
    marginBottom: theme.spacing(8),
    marginTop: theme.spacing(4),
    position: 'relative',
  }

  const location = React.useMemo(() => {
    return {
      latitude: locationLatitude,
      longitude: locationLongitude,
      bounds: [
        (locationBounds0 - 0.24) || (locationLongitude - 1),
        (locationBounds1 - 0.24) || (locationLatitude - 1),
        (locationBounds2 + 0.24) || (locationLongitude + 1),
        (locationBounds3 + 0.24) || (locationLatitude + 1),
      ] as LngLatBoundsLike,
    }
  }, [locationLatitude, locationLongitude, locationBounds0, locationBounds1, locationBounds2, locationBounds3])

  // render map into container
  useEffect(() => {
    if (containerRef.current && !mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: containerRef.current,
        style: props.dark ? 'mapbox://styles/mapbox/dark-v9' : 'mapbox://styles/mapbox/light-v9',
        attributionControl: false,
      })

      mapRef.current.addControl(new mapboxgl.NavigationControl())
    }
  }, [props.dark])

  // add location marker to map
  useEffect(() => {
    if (mapRef.current) {
      locationMarkerRef.current?.remove()

      if (location.longitude && location.latitude) {
        const markerElem = document.createElement('div')

        markerElem.style.width = '12px'
        markerElem.style.height = '12px'
        markerElem.style.borderRadius = '24px'
        markerElem.style.backgroundColor = 'red'
        markerElem.style.border = '2px solid white'

        try {
          locationMarkerRef.current = new mapboxgl.Marker(markerElem)
          locationMarkerRef.current.setLngLat([location.longitude, location.latitude]).addTo(mapRef.current)

          if (location.bounds) {
            mapRef.current?.fitBounds(location.bounds)
          }
        } catch (err) {
          // Let's not break up the signup flow with API errors.
          console.error(err)
        }
      }
    }
  }, [location])

  let style = STYLE

  if (props.style) {
    style = {
      ...STYLE,
      ...props.style,
    }
  }

  return (
    <ErrorBoundary>
      <div style={style} ref={containerRef} />
    </ErrorBoundary>
  )
}

export default FreelancerLocationMap
